class GraylogService {
    constructor() {
        this.host = 'http://graylog.tunisolutions.com';
        this.port = 12202;
        this.applicationName = 'TuniMobile-FE';
        this.environment = process.env.NODE_ENV || 'development';
        this.sensitiveFields = ['password', 'token', 'secret', 'api_key', 'authorization'];
    }


    sanitizeLogEntry(logEntry) {
        if (!logEntry) return logEntry;

        // Handle strings (could be JSON strings)
        if (typeof logEntry === 'string') {
            try {
                const parsed = JSON.parse(logEntry);
                return JSON.stringify(this.sanitizeLogEntry(parsed));
            } catch (e) {
                // Not JSON, return as is
                return logEntry;
            }
        }

        // Handle arrays
        if (Array.isArray(logEntry)) {
            return logEntry.map(item => this.sanitizeLogEntry(item));
        }

        // Handle objects
        if (typeof logEntry === 'object' && logEntry !== null) {
            const sanitized = {};
            for (const [key, value] of Object.entries(logEntry)) {
                const lowerKey = key.toLowerCase();

                // Check if this field should be masked
                const isSensitive = this.sensitiveFields.some(field =>
                    lowerKey.includes(field.toLowerCase())
                );

                if (isSensitive && value) {
                    // Mask the sensitive value
                    sanitized[key] = '****';
                } else if (typeof value === 'object') {
                    // Recursively sanitize nested objects/arrays
                    sanitized[key] = this.sanitizeLogEntry(value);
                } else {
                    sanitized[key] = value;
                }
            }
            return sanitized;
        }

        return logEntry;
    }

    /**
     * Send log to Graylog via HTTP (since UDP is not available in browsers)
     * @param {string} level - Log level (error, warning, info, debug)
     * @param {string} message - Log message
     * @param {Object} additionalData - Additional data to include in log
     */
    async sendLog(level, message, additionalData = {}) {
        const logEntry = {
            version: '1.1',
            host: window.location.hostname || 'unknown',
            short_message: this.sanitizeLogEntry(message),
            full_message: JSON.stringify(this.sanitizeLogEntry(additionalData)),
            timestamp: Math.floor(Date.now() / 1000),
            level: this.getLogLevel(level),
            facility: this.applicationName,
            environment: this.environment,
            url: window.location.href,
            userAgent: navigator.userAgent,
            userId: this.getCurrentUserId(),
            sessionId: this.getSessionId(),
            ...additionalData
        };

        try {
            // Use fetch to send log to Graylog HTTP input
            // Note: This requires Graylog to have HTTP input configured
            await fetch(`${this.host + ':' + this.port}/gelf`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(logEntry),
                mode: 'no-cors' // Required for cross-origin requests
            });

            // Since we're using no-cors mode, we can't check the response
            // The log is sent asynchronously

        } catch (error) {
            // Fallback: Send to console and try alternative method
            console.error('Failed to send log to Graylog:', error);
            console.log('Graylog Log Entry:', logEntry);

            // Try sending via image pixel as last resort (for critical errors only)
            if (level === 'error' || level === 'critical') {
                this.sendLogViaImage(logEntry);
            }
        }
    }

    /**
     * Fallback method to send logs via image pixel (for critical errors)
     * @param {Object} logEntry - Log entry to send
     */
    sendLogViaImage(logEntry) {
        try {
            const img = new Image();
            const encodedData = encodeURIComponent(JSON.stringify(logEntry));
            img.src = `${this.host}/gelf?data=${encodedData}`;
            img.style.display = 'none';
            document.body.appendChild(img);

            // Clean up after sending
            setTimeout(() => {
                if (img.parentNode) {
                    img.parentNode.removeChild(img);
                }
            }, 1000);
        } catch (error) {
            console.error('Failed to send log via image pixel:', error);
        }
    }

    /**
     * Log error with stack trace
     * @param {Error} error - Error object
     * @param {Object} context - Additional context information
     */
    logError(error, context = {}) {
        const errorData = {
            error: {
                name: error.name,
                message: error.message,
                stack: error.stack,
                ...context
            }
        };

        this.sendLog('error', `Application Error: ${error.message}`, errorData);
    }

    /**
     * Log API error
     * @param {Object} error - API error response
     * @param {Object} requestInfo - Request information
     */
    logApiError(error, requestInfo = {}) {
        const apiErrorData = {
            type: 'api_error',
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            method: error.config?.method,
            requestData: this.sanitizeLogEntry(error.config?.data),
            responseData: this.sanitizeLogEntry(error.response?.data),
            ...requestInfo
        };

        this.sendLog('error', `API Error: ${error.message}`, apiErrorData);
    }

    /**
     * Log page crash or unhandled error
     * @param {Object} error - Error information
     * @param {string} errorInfo - Additional error info (for React error boundaries)
     */
    logPageCrash(error, errorInfo = null) {
        const crashData = {
            type: 'page_crash',
            error: {
                name: error.name || 'Unknown Error',
                message: error.message || 'Page crashed',
                stack: error.stack || 'No stack trace available'
            },
            errorInfo: errorInfo,
            page: window.location.pathname,
            timestamp: new Date().toISOString()
        };

        this.sendLog('error', `Page Crash: ${error.message || 'Unknown error'}`, crashData);
    }

    // Removed user action logging - keeping only essential error logging

    /**
     * Get current user ID from Redux store or localStorage
     */
    getCurrentUserId() {
        try {
            // Try to get from Redux store if available
            const state = window.__REDUX_STORE__?.getState?.();
            if (state?.auth?.user?.id) {
                return state.auth.user.id;
            }

            // Fallback to localStorage
            const userData = localStorage.getItem('persist:root');
            if (userData) {
                const parsed = JSON.parse(userData);
                const auth = JSON.parse(parsed.auth || '{}');
                return auth.user?.id;
            }

            return 'anonymous';
        } catch (error) {
            return 'unknown';
        }
    }

    /**
     * Get or generate session ID
     */
    getSessionId() {
        let sessionId = sessionStorage.getItem('sessionId');
        if (!sessionId) {
            sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('sessionId', sessionId);
        }
        return sessionId;
    }

    /**
     * Convert string level to Graylog numeric level
     */
    getLogLevel(level) {
        const levels = {
            debug: 7,
            info: 6,
            notice: 5,
            warning: 4,
            error: 3,
            critical: 2,
            alert: 1,
            emergency: 0
        };
        return levels[level.toLowerCase()] || 6;
    }

    /**
     * Close service (cleanup method)
     */
    close() {
        // No cleanup needed for HTTP-based logging
    }
}

// Create singleton instance
const graylogService = new GraylogService();

export default graylogService;
